/**
 * MultiSelectionPanel Component
 * Shows a floating panel with selected elements list when 2+ elements are selected.
 * Allows individual deselection via checkboxes.
 */

import { useMemo } from 'react'
import { dia } from '@joint/core'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import styles from './MultiSelectionPanel.module.css'

interface MultiSelectionPanelProps {
  selectedElementIds: string[]
  graph: dia.Graph | null
  onRemoveElement: (elementId: string) => void
  onClearAll: () => void
}

export function MultiSelectionPanel({
  selectedElementIds,
  graph,
  onRemoveElement,
  onClearAll
}: MultiSelectionPanelProps) {
  const objectTypes = useObjectTypeStore(s => s.types)

  const items = useMemo(() => {
    if (!graph) return []
    return selectedElementIds.map(id => {
      const element = graph.getCell(id)
      if (!element) return null
      const data = element.get('data') || {}
      const typeId = data.typeId || (element as any).prop?.('objectTypeId')
      const objType = objectTypes.find(t => t.id === typeId)
      const typeName = objType?.name || data.layer || '알 수 없음'
      const shortId = String(id).slice(-6)
      return { id, shortId, typeName }
    }).filter(Boolean) as { id: string; shortId: string; typeName: string }[]
  }, [selectedElementIds, graph, objectTypes])

  if (items.length < 2) return null

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>선택된 객체 ({items.length})</span>
        <button className={styles.clearButton} onClick={onClearAll}>
          전체 해제
        </button>
      </div>
      <div className={styles.list}>
        {items.map(item => (
          <label key={item.id} className={styles.item}>
            <input
              type="checkbox"
              checked={true}
              onChange={() => onRemoveElement(item.id)}
              className={styles.checkbox}
            />
            <span className={styles.typeBadge}>{item.typeName}</span>
            <span className={styles.itemId}>...{item.shortId}</span>
          </label>
        ))}
      </div>
    </div>
  )
}
